import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section } from "../components/shared/Section";
import { SectionHeading } from "../components/shared/SectionHeading";
import { GlassCard } from "../components/shared/GlassCard";
import { SEO } from "../components/shared/SEO";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much does music video production cost in Guwahati?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Music video production cost in Guwahati depends on shoot duration, equipment, locations and post-production complexity. Contact Hydrox Studios for a customized quote."
      }
    },
    {
      "@type": "Question",
      "name": "Do you offer music video editing separately?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, we provide standalone music video editing services for artists who already have footage."
      }
    },
    {
      "@type": "Question",
      "name": "Can you shoot outside Guwahati?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Hydrox Studios serves clients across Assam and Northeast India depending on project scope."
      }
    },
    {
      "@type": "Question",
      "name": "Do you handle rap music video production?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, we work with rap and hip-hop artists to create high-energy cinematic music videos."
      }
    }
  ]
};
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Hydrox Studios",
  "image": "https://studio.hydroxmusic.com/og-image.jpg",
  "url": "https://studio.hydroxmusic.com/music-video-production-guwahati",
  "telephone": "+918822912060", // replace with your number
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Guwahati",
    "addressRegion": "Assam",
    "addressCountry": "IN"
  },
  "areaServed": {
    "@type": "Place",
    "name": "Assam, Northeast India"
  },
  "sameAs": [
    "https://www.instagram.com/hydroxstudios"
  ],
  "description": "Hydrox Studios is a Guwahati-based music video and audio production studio serving artists across Assam and Northeast India."
};

const combinedSchema = {
  "@context": "https://schema.org",
  "@graph": [
    localBusinessSchema,
    faqSchema
  ]
};
export default function MusicVideoProductionGuwahati() {
  return (
    <div className="text-white">
      <SEO
  title="Music Video Production in Guwahati | Professional Video Studio Assam"
  description="Looking for music video production in Guwahati? Hydrox Studios offers professional filming, editing, color grading and post-production services for artists across Assam and Northeast India."
  path="/music-video-production-guwahati"
  schema={combinedSchema}
/>

      {/* Hero */}
      <Section className="pt-32 pb-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 max-w-4xl mx-auto"
        >
          Music Video Production in Guwahati, Assam
        </motion.h1>

        <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
          Hydrox Studios is a Guwahati-based music video production studio
          delivering cinematic visuals, structured direction and disciplined
          post-production for artists across Assam and Northeast India.
        </p>
      </Section>

      {/* Introduction */}
      <Section>
        <div className="max-w-4xl mx-auto text-muted-foreground space-y-6 text-base leading-relaxed">
          <p>
            If you are searching for music video production near you in
            Guwahati, choosing the right studio makes the difference between
            average content and cinematic storytelling. Professional music
            video production requires creative direction, technical precision
            and structured workflow from start to finish.
          </p>

          <p>
            At Hydrox Studios, we manage complete end-to-end music video
            production in Guwahati — including concept development, shoot
            planning, filming, editing, color grading and final digital
            delivery. Our process is designed to ensure clarity, efficiency and
            high production value visuals that compete on national platforms.
          </p>

          <p>
            Being physically located in Guwahati allows us to execute projects
            efficiently while understanding the regional creative landscape of
            Assam. We work with independent artists, record labels and creative
            brands looking for professional music video production services in
            Northeast India.
          </p>
        </div>
      </Section>

      {/* Services Breakdown */}
      <Section className="bg-black/30">
        <SectionHeading
          caption="Our Services"
          title="Complete Music Video Production Services in Guwahati"
          description="From creative development to final delivery."
        />

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              title: "Concept & Creative Direction",
              desc: "We define the visual theme, narrative structure and artistic direction aligned with your music genre and audience."
            },
            {
              title: "Pre-Production Planning",
              desc: "Location scouting in Guwahati, scheduling, shot listing and equipment planning for efficient execution."
            },
            {
              title: "Cinematic Filming",
              desc: "Professional camera setups, controlled lighting and modern framing techniques for high production quality."
            },
            {
              title: "Music Video Editing",
              desc: "Beat-synced editing, pacing control and visual storytelling refinement by experienced editors."
            },
            {
              title: "Color Grading",
              desc: "Professional cinematic color grading to create consistent visual identity and emotional tone."
            },
            {
              title: "Digital Delivery",
              desc: "Optimized exports for YouTube, Instagram and other streaming platforms."
            }
          ].map((item, index) => (
            <GlassCard key={index} className="p-8">
              <h3 className="font-syne text-xl font-semibold mb-3">
                {item.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {item.desc}
              </p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* Why Choose */}
      <Section>
        <SectionHeading
          caption="Why Choose Us"
          title="Why Artists Choose Our Music Video Studio in Guwahati"
        />

        <div className="max-w-4xl mx-auto space-y-6 text-muted-foreground leading-relaxed">
          <p>
            There are multiple video production companies in Assam, but
            structured execution separates professional output from casual
            production. Our workflow is disciplined and organized — every
            project follows a defined process.
          </p>

          <ul className="list-disc list-inside space-y-3">
            <li>Structured pre-production planning</li>
            <li>Dedicated music video editor and post-production team</li>
            <li>Professional camera and lighting equipment</li>
            <li>Integrated audio post-production support</li>
            <li>Serving Guwahati, Assam and Northeast India</li>
          </ul>

          <p>
            Whether you are producing a rap music video, commercial release or
            artistic concept project, our studio focuses on delivering clean,
            cinematic visuals with professional finishing.
          </p>
        </div>
      </Section>

      {/* Pricing Section */}
      <Section className="bg-black/30">
        <SectionHeading
          caption="Pricing"
          title="Music Video Production Cost in Guwahati"
        />

        <div className="max-w-4xl mx-auto text-muted-foreground space-y-6 leading-relaxed">
          <p>
            Music video production cost in Guwahati varies depending on shoot
            duration, number of locations, equipment requirements, editing
            complexity and visual effects involvement.
          </p>

          <p>
            Instead of fixed generic packages, Hydrox Studios evaluates each
            project individually to align production scale with your budget and
            creative goals.
          </p>

          <p>
            If you are searching for affordable music video production near
            you, we recommend discussing your concept in detail so we can
            structure the most efficient execution strategy.
          </p>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeading
          caption="FAQs"
          title="Music Video Production in Guwahati – FAQs"
        />

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              q: "How much does music video production cost in Guwahati?",
              a: "Costs depend on production scale and post-production complexity. Contact us for a detailed evaluation."
            },
            {
              q: "Do you offer music video editing separately?",
              a: "Yes. We provide standalone music video editing services for artists who already have footage."
            },
            {
              q: "Can you shoot outside Guwahati?",
              a: "Yes. We execute projects across Assam and Northeast India depending on scope."
            },
            {
              q: "Do you handle rap music video production?",
              a: "Yes. We work with rap and hip-hop artists, ensuring high-energy cinematic visual storytelling."
            }
          ].map((faq, index) => (
            <GlassCard key={index} className="p-8">
              <h4 className="font-syne text-lg font-semibold mb-3">
                {faq.q}
              </h4>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {faq.a}
              </p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="text-center pb-24">
        <h2 className="font-syne text-3xl md:text-4xl font-bold mb-6">
          Start Your Music Video Production in Guwahati
        </h2>

        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
          Contact Hydrox Studios today and transform your next release into a
          cinematic visual experience.
        </p>

        <Link to="/contact">
          <Button size="lg" className="px-8 py-6">
            Get a Quote
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </Link>
      </Section>
    </div>
  );
}